/**
 * The elbow (or straight) connector strip between two bracket round columns. For an elbow it draws
 * one hook per child — two parents on `side` merging into a single lead toward the child column.
 * Straight is the 1→1 line used to reach the centre Final. Geometry comes from bracketGeometry so
 * the hooks always meet the box slots without any measurement.
 */

import React, { useMemo } from 'react'
import { View } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import {
  connectorElementStyle,
  elbowStyles,
  straightStyle,
  trackStyle,
  type ConnectorSide,
} from '../../../src/knockout/helpers/bracketGeometry.helper'
import { BRACKET_CONNECTOR_W } from './knockoutBracket.constants'
import { getBracketConnectorStyles } from './BracketConnector.styles'

interface BracketConnectorProps {
  trackH: number
  /** Number of children (centre-side boxes) this strip feeds. */
  childCount: number
  /** Which side the two parents sit on. */
  side: ConnectorSide
  /** Straight 1→1 line (used into the centre Final) instead of a 2→1 elbow. */
  straight?: boolean
  /** Strip width (column gap). Grows on wide screens so the bracket fills the container. */
  connectorW?: number
}

export const BracketConnector: React.FC<BracketConnectorProps> = ({
  trackH,
  childCount,
  side,
  straight = false,
  connectorW = BRACKET_CONNECTOR_W,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getBracketConnectorStyles(theme), [theme])
  const widthStyle = useMemo(() => ({ width: connectorW }), [connectorW])

  const count = Math.max(childCount, 1)
  const h = trackH / count

  return (
    <View style={[styles.track, trackStyle(trackH), widthStyle]} pointerEvents="none">
      {Array.from({ length: count }, (_, i) => {
        if (straight) {
          return (
            <View key={`connector-${side}-straight-${i}`} style={connectorElementStyle(trackH, count)}>
              <View style={[styles.fillLine, straightStyle(h)]} />
            </View>
          )
        }
        const elbow = elbowStyles(h, side, connectorW)
        return (
          <View key={`connector-${side}-elbow-${i}`} style={connectorElementStyle(trackH, count)}>
            <View style={[styles.elbowLine, elbow.top]} />
            <View style={[styles.elbowLine, elbow.bottom]} />
            <View style={[styles.fillLine, elbow.lead]} />
          </View>
        )
      })}
    </View>
  )
}
